import React, { memo, MouseEvent, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { DynamicIconWrapper } from '@components/common';

import { Icons } from '@icons/icons';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { isEmpty } from '@utils/common';

import { IPortalFolder } from '@interfaces/portal/folders';
import { IFolderActionDTO } from './interfaces';

import { EFolderActionsId, getFolderActions } from './constants';

import FolderItemActionsMenu from './FolderItemActionsMenu';

import styles from './FolderItem.module.scss';

const ICON_SIZE = '20';

interface IFolderItemProps {
	folder: IPortalFolder;
	onSelect: (id: string) => void;
	selectedFolderId: string | null;
	expandedFolderId: string | null;
	expandFolder: (id: string) => void;
	onFolderActionClick?: (data: IFolderActionDTO) => void;
	depth?: number;
	isSingleFolder: boolean;
	isProjectActive: boolean;
	isPortalAdmin: boolean;
}

const FolderItem = ({
	folder,
	onSelect,
	selectedFolderId,
	expandedFolderId,
	expandFolder,
	onFolderActionClick,
	depth = 0,
	isSingleFolder,
	isProjectActive,
	isPortalAdmin,
}: IFolderItemProps) => {
	const actionsMenuWrapperRef = useRef<HTMLDivElement>(null);

	const {
		id: folderId,
		name: folderName,
		hasContent: folderHasContent,
		subfolders,
		isFileMatch,
		fileMatchCount,
		parentFolderId,
	} = folder;

	const isParentFolder = depth === 0;

	const [isVisibleActionsMenu, setIsVisibleActionsMenu] = useState(false);

	const closeActionsMenu = () => setIsVisibleActionsMenu(false);

	const toggleVisibleActionsMenu = (e: MouseEvent) => {
		e.stopPropagation();

		setIsVisibleActionsMenu(prevState => !prevState);
	};

	useOnClickOutside({ refs: [actionsMenuWrapperRef], handler: () => setIsVisibleActionsMenu(false) });

	const onClickActionsMenuItem = (action: EFolderActionsId) => {
		if (!onFolderActionClick) {
			return;
		}

		onFolderActionClick({
			action,
			folderId,
			isParentFolder,
			folderName,
			folderHasContent,
			parentFolderId,
		});
	};

	const onExportClick = () => {
		if (!onFolderActionClick) {
			return;
		}

		onFolderActionClick({
			action: EFolderActionsId.download,
			folderId,
			isParentFolder,
			folderName,
			folderHasContent,
			parentFolderId,
		});
	};

	const isSelected = folderId === selectedFolderId;
	const hasChildSelected = subfolders?.some(subfolder => subfolder.id === selectedFolderId);
	const isExpanded = folderId === expandedFolderId;

	const onFolderClick = () => {
		onSelect(folderId);
	};

	useEffect(() => {
		if (isParentFolder && (isSelected || hasChildSelected)) {
			expandFolder(folderId);
		}
	}, [
		isParentFolder,
		expandFolder,
		folderId,
		isSelected,
		hasChildSelected,
	]);

	const exportIcon = () => Icons.export({ height: ICON_SIZE, width: ICON_SIZE });

	return (
		<div className={styles.folderItemContainer}>
			<div
				className={cn(styles.folderItem, { [styles.selectedFolder]: isSelected })}
				onClick={onFolderClick}
				style={{ paddingLeft: depth ? depth * 25 : 10 }}
			>
				<div className={styles.folderName}>
					<div className={styles.icon}>
						{folderHasContent ? Icons.folderIcon() : Icons.folderEmpty()}
					</div>
					{folderName}
					{isFileMatch
						&& <div className={styles.fileMatchCount}>
							{`${fileMatchCount} ${fileMatchCount === 1 ? 'file' : 'files'} found`}
						</div>
					}
				</div>
				<div className={styles.actionsContainer}>
					{!isProjectActive
						&& <div className={styles.actionsIcon}>
							<DynamicIconWrapper
								icon={exportIcon}
								onClick={onExportClick}
								tooltipLabel='Export'
								tooltipPlacement='top'
							/>
						</div>
					}
					{isProjectActive
						&& <div
							ref={actionsMenuWrapperRef}
							className={styles.actionsIcon}
							onClick={toggleVisibleActionsMenu}
						>
							<DynamicIconWrapper
								icon={Icons.actions}
								isActive={isVisibleActionsMenu}
							/>
							<FolderItemActionsMenu
								actions={getFolderActions({
									isAddActionHidden: !isParentFolder,
									isMoveActionHidden: (isParentFolder && !isEmpty(subfolders)) || isSingleFolder,
									isDeleteActionHidden: !isPortalAdmin,
								})}
								anchorRef={actionsMenuWrapperRef}
								isVisibleMenu={isVisibleActionsMenu}
								onClick={onClickActionsMenuItem}
								onClose={closeActionsMenu}
							/>
						</div>
					}
					<div className={cn(styles.chevronIcon, { [styles.chevronIconExpanded]: isExpanded })}>
						{isParentFolder && Icons.chevronRight()}
					</div>
				</div>
			</div>
			{isExpanded && subfolders?.map((sub) => (
				<FolderItem
					key={sub.id}
					depth={depth + 1}
					expandFolder={expandFolder}
					expandedFolderId={expandedFolderId}
					folder={sub}
					isPortalAdmin={isPortalAdmin}
					isProjectActive={isProjectActive}
					isSingleFolder={isSingleFolder}
					onFolderActionClick={onFolderActionClick}
					onSelect={onSelect}
					selectedFolderId={selectedFolderId}
				/>
			))}
		</div>
	);
};

export default memo(FolderItem);
